import { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldAlert, ShieldCheck, Loader2, Wrench } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * WalletHealth — surfaces "orphan" agents tied to the wallet but linked to a
 * stale user_id (e.g., after a DB restore). Reads /admin/users/{addr}/wallet-snapshot
 * and offers one-click self-service heal via /admin/users/{addr}/heal-orphan-agents.
 *
 * Renders nothing (null) when the wallet is healthy — only shows when there's
 * an actual issue to surface. Keeps the profile page clean for normal users.
 */
export default function WalletHealth({ stxAddress, onHealed }) {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [healing, setHealing] = useState(false);

  const load = useCallback(async () => {
    if (!stxAddress) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API}/api/admin/users/${stxAddress}/wallet-snapshot`
      );
      const data = await res.json();
      if (data?.success) setSnapshot(data);
    } catch {
      // silent — diagnostic only
    } finally {
      setLoading(false);
    }
  }, [stxAddress]);

  useEffect(() => {
    load();
  }, [load]);

  const orphans = snapshot?.summary?.agents_orphan_user_id || 0;
  if (loading && !snapshot) return null;
  if (!snapshot || orphans === 0) return null; // healthy → render nothing

  const heal = async () => {
    setHealing(true);
    try {
      const res = await fetch(
        `${API}/api/admin/users/${stxAddress}/heal-orphan-agents`,
        { method: "POST" }
      );
      const data = await res.json();
      if (data?.success) {
        toast.success(
          `Reassigned ${data.agents_reassigned} orphan agent${
            data.agents_reassigned !== 1 ? "s" : ""
          } to your wallet`
        );
        await load();
        onHealed?.();
      } else {
        toast.error("Heal failed — check console");
      }
    } catch (e) {
      toast.error(`Heal failed: ${e?.message || e}`);
    } finally {
      setHealing(false);
    }
  };

  return (
    <Card
      className="border-orange-500/40 bg-orange-500/5"
      data-testid="wallet-health-card"
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-orange-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h3
              className="font-semibold text-orange-300"
              data-testid="wallet-health-title"
            >
              {orphans} orphan agent{orphans !== 1 ? "s" : ""} detected
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              {orphans} agent{orphans !== 1 ? "s are" : " is"} linked to your wallet but
              tied to a stale user record (probably a DB migration). One click
              reassigns {orphans !== 1 ? "them" : "it"} to your current
              account.
            </p>
            <div className="flex items-center gap-2 mt-3">
              <Button
                size="sm"
                onClick={heal}
                disabled={healing}
                data-testid="wallet-health-heal-button"
                className="bg-orange-500 hover:bg-orange-600 text-black"
              >
                {healing ? (
                  <>
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    Healing…
                  </>
                ) : (
                  <>
                    <Wrench className="w-3 h-3 mr-1" />
                    Reassign {orphans} agent{orphans !== 1 ? "s" : ""}
                  </>
                )}
              </Button>
              <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" />
                Safe — only touches agents that match this wallet
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
